import {LegacyWBIcon} from '@wandb/common/components/elements/LegacyWBIcon';
import classNames from 'classnames';
import _ from 'lodash';
import React, {
  memo,
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import {Input, Popup} from 'semantic-ui-react';

import {escapeRegexChars} from '../util/regex';

interface RunsSearchBarProps {
  isGroupSearch: boolean;
  isInReport?: boolean;
  searchQuery: string;
  setSearch(search: string): void;
}

const RunsSearchBarComp: React.FC<RunsSearchBarProps> = ({
  isGroupSearch,
  isInReport,
  searchQuery: propsSearchQuery,
  setSearch,
}) => {
  const [focused, setFocused] = useState(false);
  const [inputValue, setInputValue] = useState(propsSearchQuery);
  const [regex, setRegex] = useState(true);
  const inputRef = useRef<Input | null>(null);
  const dontBlurRef = useRef(false);

  const search = useMemo(
    () =>
      _.debounce((value: string) => {
        const newSearch = regex ? value : escapeRegexChars(value);
        if (newSearch !== propsSearchQuery) {
          setSearch(newSearch);
          const actionLocation = isInReport ? 'report' : 'runs table';
          window.analytics?.track('Runs searched', {
            location: actionLocation,
          });
        }
      }, 500),
    [regex, setSearch, propsSearchQuery, isInReport]
  );

  const toggleRegex = useCallback(() => {
    // HAX: prevent the search bar from blurring
    dontBlurRef.current = true;
    setTimeout(() => {
      dontBlurRef.current = false;
      inputRef.current?.focus();
    });

    setRegex(prev => !prev);
  }, []);

  useEffect(() => {
    search(inputValue.trim());
  }, [regex, inputValue, search]);

  const className = [
    'runs-search-bar-input',
    focused || inputValue.length > 0 ? 'active' : 'inactive',
  ].join(' ');

  return (
    <div className="runs-search-bar">
      <Input
        ref={inputRef}
        className={className}
        icon={{
          className: 'wbic-ic-search',
          size: 'large',
        }}
        iconPosition="left"
        value={inputValue}
        placeholder={
          focused
            ? isGroupSearch
              ? 'Find matching groups'
              : 'Find matching runs'
            : undefined
        }
        onChange={(e, {value}) => {
          setInputValue(value);
        }}
        onFocus={() => {
          setFocused(true);
        }}
        onBlur={() => {
          if (dontBlurRef.current) {
            return;
          }
          setFocused(false);
          setInputValue(q => q.trim());
        }}
      />
      <Popup
        className="runs-search-bar-regex-toggle-popup"
        content="Toggle regex matching"
        position="top center"
        popperModifiers={{
          preventOverflow: {
            boundariesElement: 'viewport',
          },
        }}
        inverted
        trigger={
          <LegacyWBIcon
            name="regex"
            className={classNames('runs-search-bar-regex-toggle', {
              active: regex,
            })}
            onMouseDown={toggleRegex}
          />
        }
      />
    </div>
  );
};

export const RunsSearchBar = memo(RunsSearchBarComp);
